import { useCallback, useEffect, useMemo, useState } from 'react';

export function getScrollPosition(element?: HTMLElement | null) {
  const isBrowser = typeof window !== 'undefined';

  if (!isBrowser) {
    return { x: 0, y: 0 };
  }

  const defaultValue = { x: window.scrollX, y: window.scrollY };

  if (element) {
    const children = element.children;
    const list = children[0];

    if (!list) {
      return defaultValue;
    }

    const containerPosition = element.getBoundingClientRect();
    const listPosition = list.getBoundingClientRect();

    return {
      x: listPosition.left - containerPosition.left,
      y: listPosition.top - containerPosition.top,
    };
  }

  return defaultValue;
}

export function useScroll(element?: HTMLElement | null, stopScrollDelay = 3000) {
  type ScrollDirectionY = 'down' | 'up';
  type ScrollDirectionX = 'left' | 'right';
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [scrollDirectionY, setScrollDirectionY] = useState<ScrollDirectionY>('up');
  const [scrollDirectionX, setScrollDirectionX] = useState<ScrollDirectionX>('left');
  const [isScrolling, setIsScrolling] = useState(true);

  const scrollListener = useCallback(() => {
    const newScrollPosition = getScrollPosition(element);
    setScrollDirectionY(newScrollPosition.y > scrollPosition.y ? 'down' : 'up');
    setScrollDirectionX(newScrollPosition.x > scrollPosition.x ? 'right' : 'left');
    setScrollPosition(newScrollPosition);
  },
  [element, scrollPosition.x, scrollPosition.y]);

  useEffect(() => {
    if (element) {
      element.addEventListener(
        'scroll',
        scrollListener,
        false,
      );
    } else {
      window.addEventListener(
        'scroll',
        scrollListener,
        false,
      );
    }

    return () => {
      if (element) {
        element.removeEventListener(
          'scroll',
          scrollListener,
          false,
        );
      } else {
        window.removeEventListener(
          'scroll',
          scrollListener,
          false,
        );
      }
    };
  },
  [scrollPosition, element, scrollListener]);

  useEffect(() => {
    if (!isScrolling) {
      setIsScrolling(true);
    }

    const timeout = setTimeout(() => {
      setIsScrolling(false);
    },
    stopScrollDelay);

    return () => {
      clearTimeout(timeout);
    };
  },
  // intentionally leaving out isScrolling here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [scrollPosition]);

  return useMemo(() => ({
    isScrolling,
    scrollDirectionX,
    scrollDirectionY,
    scrollPosition,
  }),
  [isScrolling, scrollDirectionX, scrollDirectionY, scrollPosition]);
}
